import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { fetchApi } from '../lib/api';

export function useCalendlyConnection() {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [calendlyUserUri, setCalendlyUserUri] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkConnection = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('calendly_connections')
        .select('calendly_user_uri')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      setIsConnected(!!data);
      setCalendlyUserUri(data?.calendly_user_uri || null);
    } catch (err) {
      console.error('Error checking Calendly connection:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Check for redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendly') === 'connected') {
      checkConnection();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkConnection]);

  const connectCalendly = useCallback(async () => {
    try {
      const res = await fetchApi('/api/auth/calendly');
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert('Failed to start Calendly connection: ' + err.message);
    }
  }, []);

  const disconnectCalendly = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('calendly_connections')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setIsConnected(false);
      setCalendlyUserUri(null);
    } catch (err) {
      console.error('Error disconnecting Calendly:', err);
    }
  }, [user]);

  return { isConnected, calendlyUserUri, loading, connectCalendly, disconnectCalendly, refresh: checkConnection };
}
